// 3rd party
import React, { ReactElement, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useDispatch } from 'react-redux';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

// Components
import {
  AvgLeadTimeCell,
  CustomerPrice,
  InformationTooltip,
  Button,
  Checkbox,
  TooltipWrapper
} from 'components';
// Importing Typography separately so it plays nicely with Styled Components extension
import Typography from 'components/Typography/Typography';

// Types
import { User, TableColumnConfigs, Machine } from 'types';
import { Part, Product, PartDataWithKey, TableRow, AlternateRow, PartDetail } from 'types/parts';

// Custom hooks
import { useLanguage } from 'providers';

// API
import { useAddProductToCartMutation, useGetMachineByIdQuery } from 'api';

// Constants
import { AnalyticsCategories, ECommerceAnalyticEventActions } from 'constants/analytics';
import { sfEnabled } from 'constants/featureFlags';
import { UNIVERSAL_PRODUCTS_KEY } from 'constants/cart';

// State management
import { CartItem, CartState } from 'types/parts/cart';
import { useCart, useUser } from 'selectors';
import { cartActions } from 'actions';

// Helpers
import { getStockText } from 'common/helpers/v1';
import { generateAnalyticsEvent } from 'common/helpers/v1/analytics';
//TanStack table
import NewBaseTable, {
  ColumnConfig,
  NewBaseTableFetchParams,
  NewBaseTableFetchResult
} from 'components/NewBaseTable/NewBaseTable';

import { CellContext } from '@tanstack/react-table';
import { tanStackTableActions as tanStackActions } from 'actions/tanStackTable';
import { tanStackTableFilterOption } from 'reducers/tanStackTable';

import CustomLoader from 'components/CustomLoader';
import Eye from 'icons/indexed-icons/Eye';
import ShoppingCartAdd from 'icons/indexed-icons/ShoppingCartAdd';
import theme from 'themes';
import { CartIconBtn, TableContainer } from 'pages/PartsCatalogPage/PartsShop.element';
import PartViewFlyout from './PartsView/PartViewFlyout';
import { ToastMsg } from 'common/components/Toast';
import PartsPopupCard from './PartsView/PartsPopupCard';
import Trash from 'icons/indexed-icons/Trash';
import { PAGE_LENGTH } from 'constants/search';
import { isPriceMissing } from 'components/CustomerPrice';
import PriceRequestComponent from './PriceRequest';
import {useAddProductToRemoteCartMutation } from 'api'
import { mapBusinessUnitId } from 'common/helpers/v1/machine';
import { ComposeCartItem } from './CartHelp';

/* Interfaces used for styling and local objects */
interface MachinePartsTableProps {
  data: Part[];
  title?: string;
  showHeader?: boolean;
  isDataLoading?: boolean;
  showSearch?: boolean;
  machineId?: string;
  updateTableOnFilterChange?: (option: string) => void;
  isShowGlobalSearch?: boolean;
  handleSearch?: (inputValue?: string) => void;
  isSavedPartsTable?: boolean;
  handleRemovePart?: (part: Product) => void;
  customFetchFunction: (params: NewBaseTableFetchParams) => Promise<NewBaseTableFetchResult>;
  renderRibbon?: () => JSX.Element;
}

export type HandleRowClickFn = {
  part?: Product;
  action?: string;
};
/* End interfaces */

/* Styling */
const RowCtaContainer = styled.div`
  width: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1.5rem;
  .icon {
    width: 1rem;
  }
`;

const Root = styled.div`
  width: 100%;
  height: auto;
  .parts-table thead {
    z-index: 2;
  }
  .parts-table thead th {
    position: relative;
    z-index: 2;
    background: ${({ theme }) => theme.colors.white};
  }
  .icon--information-circle {
    width: 16px;
    height: 16px;
    margin-right: 4px;
  }
  .contact-jbt-info {
    flex-direction: row;
    width: max-content;
  }
`;

const PartDescription = styled(Typography)<{ isAlternate?: boolean }>`
  margin-left: ${({ isAlternate }) => (isAlternate ? '0rem' : '0rem')};
  display: flex;
  justify-content: space-between;
`;

const StockText = styled.div`
  white-space: nowrap;
`;

const DescriptionText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-right: 0.26rem;
  max-width: 12rem;
  cursor: pointer;
`;

const AlternativeContainer = styled.div`
  color: ${(props) => props.theme.colors.onTrackGreen};
`;
const PartNumberText = styled.div`
  z-index: 1;
`;
const ColumnToolTip = styled.div`
  position: absolute;
  z-index: 1000;
  margin-top: -1.5rem;
  margin-left: 6rem;
  display: none;
  ${PartNumberText}:hover & {
    display: block;
  }
  border: 1px solid white;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 0.25rem;
  background-color: white;
`;
const CheckboxWrapper = styled.div`
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 2;
`;
/* Helper functions */
const DivClickable = styled.div`
  max-width: fit-content;
  position: relative;
  z-index: 0;
`;
const GroupRowContainer = ({ data }: { data: AlternateRow }): ReactElement => {
  const theme = useTheme();
  return (
    <Typography weight="medium" mb={0}>
      <div style={{ color: theme.colors.darkRed }}>Discontinued</div>
      <div>{data.description}</div>
    </Typography>
  );
};

const getAttributeValue = (data: TableRow, attrName: string) => {
  return data?.alternateProduct ? data?.alternateProduct[attrName] : data[attrName];
};

const getDescriptionAttributeValue = (data: TableRow, languageId: string) => {
  const multilingualDescObj = data?.alternateProduct
    ? data?.alternateProduct['multilingualDesc']
    : data['multilingualDesc'];

  type MultiligualLanguageKey = keyof typeof multilingualDescObj;
  const multilingualDesc = multilingualDescObj
    ? multilingualDescObj[languageId as MultiligualLanguageKey]
    : undefined;
  const description = getAttributeValue(data, 'description') as string;
  return multilingualDesc ? multilingualDesc : description;
};

type Props = {
  handleClickCheckBox?: (params: HandleRowClickFn) => void;
  isDisable?: boolean;
  isSelectedAll?: boolean;
};
const HeaderCheckBox = ({ handleClickCheckBox, isDisable, isSelectedAll }: Props) => {
  return (
    <CheckboxWrapper>
      <Checkbox
        width={18}
        height={18}
        checked={isSelectedAll}
        onChange={() => {
          handleClickCheckBox?.({ action: 'selectAll' });
        }}
        margin="0"
        disabled={isDisable}
      />
    </CheckboxWrapper>
  );
};
// Generate the configurations for each column of this table
const generateColumnConfigs = (
  t: TFunction<'fpns'[], undefined>,
  addToCartHandler: (product: Product) => void,
  isLoading: boolean,
  selectedCartItem: Product,
  setSelectedCartItem: React.Dispatch<React.SetStateAction<Product | undefined>>,
  machineId?: string,
  languageId?: string,
  handlePartDetailModal?: (ProductDetail: PartDetail) => void,
  isSavedPartsTable?: boolean,
  handleRemovePart?: (part: Product, hideToast?: boolean) => void,
  handleRowClick?: (params: HandleRowClickFn) => void,
  selectedTableRows?: { selectedRows: Product[]; isSelectedAll?: boolean }
): ColumnConfig[] => {
  return [
    ...(isSavedPartsTable
      ? []
      : [
          {
            id: 'select',
            header: (
              <HeaderCheckBox
                isSelectedAll={selectedTableRows?.isSelectedAll}
                handleClickCheckBox={handleRowClick}
              />
            ),
            isEnableSorting: false,
            isSelected: true,
            renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
              // const row = cellValue.row.original as UserTypes;
              const product = cellValue.row.original as TableRow;
              const isSelected = !!selectedTableRows?.selectedRows?.find(
                (prod) => prod.id === product.id
              );
              return (
                <DivClickable>
                  {product.isPriceMissing ? (
                    <Checkbox
                      width={18}
                      height={18}
                      margin="0"
                      checked={isSelected}
                      id={product.id}
                      onChange={() =>
                        handleRowClick &&
                        handleRowClick({
                          part: product,
                          action: isSelected ? 'deselectCheckbox' : 'selectCheckbox'
                        })
                      }
                    />
                  ) : (
                    <></>
                  )}
                </DivClickable>
              );
            }
          }
        ]),
    {
      id: 'product',
      header: t('parts_table_product') as string,
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const tableRow = cellValue.row.original as TableRow;
        let children = cellValue.getValue() as unknown as ReactElement;
        if (tableRow?.rowType === 'alternate') {
          children = <GroupRowContainer data={tableRow as AlternateRow} />;
        } else {
          const val = getDescriptionAttributeValue(tableRow, languageId as string) as string;
          children = (
            <PartDescription mb={0} isAlternate={tableRow.alternateProduct}>
              {tableRow.alternateProduct && (
                <AlternativeContainer>Alternative</AlternativeContainer>
              )}
              <InformationTooltip
                tooltipText={val}
                placement="top"
                icon={
                  <Button variant="text">
                    <DescriptionText className="product-desc">{val}</DescriptionText>
                  </Button>
                }
              />
            </PartDescription>
            // <div>test</div>
          );
        }
        return children;
      }
    },
    {
      id: 'partNumber',
      header: t('parts_table_sku') as string,
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const tableRow = cellValue.row.original as TableRow;
        const colVal = tableRow.stockCode || tableRow.sku;
        const attr = tableRow.stockCode ? 'stockCode' : 'sku';
        const children = (
          <div
            style={{ cursor: 'pointer' }}
            onClick={() =>
              !isSavedPartsTable &&
              handlePartDetailModal?.({
                productId: tableRow.id,
                priceUnit: tableRow.priceUnit,
                machineId: machineId,
                ...tableRow
              })
            }
          >
            {tableRow?.rowType === 'alternate' ? (
              <>{colVal as string}</>
            ) : (
              <PartNumberText>
                <Typography
                  color={theme.colors.primaryBlue5}
                  mb={0}
                  style={{ whiteSpace: 'normal' }}
                >
                  {getAttributeValue(tableRow, attr) as string}
                </Typography>
                <ColumnToolTip>
                  <PartsPopupCard
                    priceUnit={tableRow.priceUnit}
                    machineId={tableRow.machineId}
                    custNum={tableRow.custNum}
                    {...tableRow}
                  />
                </ColumnToolTip>
              </PartNumberText>
            )}
          </div>
        );
        return children;
      }
    },
    {
      id: 'stock',
      header: t('parts_table_stock') as string,
      isEnableSorting: false,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const product = cellValue.row.original as Product;
        return <StockText>{getStockText(product, t)}</StockText>;
      }
    },
    {
      id: 'leadTime',
      header: t('parts_table_avg_lead_time') as string,
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const product = cellValue.row.original as Product;
        return <AvgLeadTimeCell purchasable={product} />;
      }
    },
    {
      id: 'price',
      header: t('parts_table_price') as string,
      isEnableSorting: true,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const record = cellValue.row.original as TableRow;
        return (
          <CustomerPrice
            product={record}
            fontSizeInREM={0.813}
            marginBottomInREM={0}
            inline={true}
          />
        );
      }
    },
    isSavedPartsTable
      ? {
          id: 'quantity',
          header: t('quantity') as string,
          isEnableSorting: true,
          isSelected: true,
          renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
            const tableRow = cellValue.row.original as TableRow;
            return <span style={{ padding: '0 1.5rem' }}>{tableRow.quantity}</span>;
          }
        }
      : {
          id: 'site',
          header: t('parts_table_site') as string,
          isEnableSorting: true,
          isSelected: true,
          renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
            const tableRow = cellValue.row.original as TableRow;
            const colVal = tableRow?.businessUnit
              ?.split('')
              .map((char, index) => (index === 0 ? char.toUpperCase() : char))
              .join('');
            return colVal;
          }
        },
    {
      id: `action`,
      header: 'Action',
      isEnableSorting: false,
      isSelected: true,
      renderer: (cellValue: CellContext<TableColumnConfigs, string>) => {
        const product = cellValue.row.original as Product;
        return (
          <RowCtaContainer>
            <CartIconBtn
              disabled={isLoading && selectedCartItem.id === product.id}
              onClick={() => {
                setSelectedCartItem(product);
                addToCartHandler(product);
                if (isSavedPartsTable) {
                  handleRemovePart?.(product, true);
                }
              }}
            >
              {isLoading && selectedCartItem.id === product.id ? (
                <CustomLoader size="1rem" thickness=".2rem" />
              ) : (
                <InformationTooltip
                  tooltipText="Add to the cart"
                  placement="top"
                  icon={
                    <Button variant="text">
                      <ShoppingCartAdd />
                    </Button>
                  }
                />
              )}
            </CartIconBtn>
            {isSavedPartsTable ? (
              <TooltipWrapper
                Tooltip={'Remove part'}
                backGroundColor="white"
                textColor="black"
                maxWidth="15rem"
                left
              >
                <Button variant="text" onClick={() => handleRemovePart?.(product)}>
                  <Trash color={theme.colors.error200} />
                </Button>
              </TooltipWrapper>
            ) : (
              <InformationTooltip
                tooltipText="View part"
                placement="top"
                icon={
                  <Button
                    variant="text"
                    onClick={() =>
                      handlePartDetailModal?.({
                        productId: product.id,
                        priceUnit: product.priceUnit,
                        machineId: machineId,
                        ...product
                      })
                    }
                  >
                    <Eye />
                  </Button>
                }
              />
            )}
          </RowCtaContainer>
        );
      }
    }
  ];
};

const addKeysToPartData = (keyPrefix: string, data: Part[]): PartDataWithKey[] => {
  return data.map((item) => {
    return {
      ...item,
      partNumber: item.stockCode || item.sku,
      key: `${keyPrefix}-${item.sku}`
    };
  });
};

const generateAlternateParts = (partRows: PartDataWithKey[]): TableRow[] => {
  const rows: TableRow[] = [];
  partRows.forEach((partRow) => {
    if (partRow.alternateSku && partRow.alternateProduct) {
      rows.push({
        rowType: 'alternate',
        ...partRow
      });
    }
    rows.push(partRow);
  });
  return rows;
};

/* End helper functions */

/* Initial states for sorting and filtering */
const defaultSortState = {
  id: 'description',
  desc: true
};
const filterOptions: tanStackTableFilterOption[] = [
  { value: 'low_stock', label: 'Low Stock' },
  { value: 'in_stock', label: 'In Stock' },
  { value: 'out_stock', label: 'Out of Stock' }
];
/* End initial states */

const MachinePartsTable = ({
  data,
  title,
  showHeader = true,
  machineId = undefined,
  updateTableOnFilterChange,
  isShowGlobalSearch,
  handleSearch,
  isSavedPartsTable,
  handleRemovePart,
  customFetchFunction,
  renderRibbon
}: MachinePartsTableProps): ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation(['fpns']);

  const { languageId } = useLanguage();
  const keyPrefix = 'm-parts';
  const [partDetail, setPartDetail] = useState<PartDetail>({
    partFlyout: false,
    productId: '',
    priceUnit: ''
  });
  const [selectedCartItem, setSelectedCartItem] = useState<Product>();
  const [selectedTableRows, setSelectedTableRows] = useState<{
    selectedRows: Product[];
    isSelectedAll: boolean;
  }>();
  const [AddProductToRemoteCart] = useAddProductToRemoteCartMutation();
  const dataWithKeys: PartDataWithKey[] = addKeysToPartData(keyPrefix, data);

  const dataWithAlternates: TableRow[] = generateAlternateParts(dataWithKeys);

  const user = useUser() as User;
  const cart = useCart() as CartState;
  const [addProductToCart, addCartResult] = useAddProductToCartMutation();

  const { data: machine } = useGetMachineByIdQuery(machineId ?? skipToken);

  //tanStack table filter options
  dispatch(tanStackActions.addFilterOptions(filterOptions));

  const addToCartHandler = (product: Product) => {
    product.isPriceMissing = product.isPriceMissing || isPriceMissing(product);
    try {
      if (sfEnabled()) {
        addProductToCart({
          // Use the machine's plant ID in favor of the user's first plant if available
          accountId: machine ? machine.plantId : user.plants[0],
          cart: cart,
          product: product,
          productId: product.sfProductId as string,
          quantity: 1,
          machineId: machineId,
          machineDescription:
            machine === undefined || machineId === UNIVERSAL_PRODUCTS_KEY
              ? UNIVERSAL_PRODUCTS_KEY
              : machine?.description
        });
      } else {
       
        AddProductToRemoteCart([ComposeCartItem(product, machineId as string, machine as Machine)] as CartItem[])
              .unwrap()
              .then(() => {
                // maintain temporay FE cartState to track cart count
                 dispatch({
                    type: cartActions.ADD_TO_CART,
                    item: product,
                    quantity: product.quantity,
                    groupId: machineId,
                    groupDescription:
                      machine === undefined || machineId === UNIVERSAL_PRODUCTS_KEY
                        ? UNIVERSAL_PRODUCTS_KEY
                        : machine?.description
                  });
                             
              })
              .catch((error) => {
                console.warn('Error adding item to cart: ', error);
              });
          
       
      }
      generateAnalyticsEvent({
        category: AnalyticsCategories.ECOMMERCE,
        action: ECommerceAnalyticEventActions.ADD_TO_CART_FROM_SEARCH_RESULTS,
        event: ECommerceAnalyticEventActions.ADD_TO_CART,
        currency: product.priceUnit,
        value: product.custPrice,
        items: [
          {
            item_id: product.id,
            item_name: product.description,
            quantity: product.stock,
            price: product.custPrice,
            currency: product.priceUnit
          }
        ]
      });
    } catch (error) {
      console.warn('Add to cart failed! Error:', error);
      ToastMsg({
        heading: 'Error',
        message: t('problem_adding_to_cart', { item: product.sku }),
        type: 'error'
      });
    }
  };

  const handlePartDetailModal = (partDetail: PartDetail) => {
    setPartDetail({ ...partDetail, partFlyout: true });
  };
  const addKeysToData = (row: Record<string, unknown>) => {
    return { isPriceMissing: isPriceMissing(row as Product) };
  };

  const handleRowClick = ({ part = {} as Product, action }: HandleRowClickFn) => {
    switch (action) {
      case 'selectCheckbox':
        setSelectedTableRows({
          selectedRows: [...(selectedTableRows?.selectedRows || []), part],
          isSelectedAll: false
        });
        break;
      case 'deselectCheckbox':
        setSelectedTableRows({
          selectedRows:
            selectedTableRows?.selectedRows?.filter((product) => product.id !== part?.id) || [],
          isSelectedAll: false
        });
        break;
      case 'selectAll':
        setSelectedTableRows({
          selectedRows: selectedTableRows?.isSelectedAll
            ? []
            : data.filter((product) => isPriceMissing(product)),
          isSelectedAll: !selectedTableRows?.isSelectedAll
        });
    }
  };

  return (
    <Root>
      {showHeader && <>{title && <h2>{title}</h2>}</>}
      <TableContainer>
        <NewBaseTable
          newTableData={dataWithAlternates}
          columnConfigs={generateColumnConfigs(
            t,
            addToCartHandler,
            addCartResult.isLoading,
            selectedCartItem as Product,
            setSelectedCartItem,
            machineId,
            languageId,
            handlePartDetailModal,
            isSavedPartsTable,
            handleRemovePart,
            handleRowClick,
            selectedTableRows
          )}
          tdMarginLeft="0.625rem"
          textAlign="left"
          sortState={defaultSortState}
          enableFilter={!isSavedPartsTable}
          enableFilterOptionsSearch={!!isSavedPartsTable}
          isShowColumnOptions={!isSavedPartsTable}
          handleFilterRequest={updateTableOnFilterChange}
          // isDataLoading={isDataLoading}
          isShowGlobalSearch={isShowGlobalSearch}
          searchPlaceholder={t('product_name_or_part_number') as string}
          customClassName="parts-table"
          searchInputValue={handleSearch}
          hideTotalFetchCount={isSavedPartsTable}
          enableServerSide={isSavedPartsTable ? false : true}
          ApiFetchSize={PAGE_LENGTH.LARGE}
          customFetchFunction={customFetchFunction}
          addKeysToTableData={addKeysToData}
          tableHeadBtn={
            <>
              {!isSavedPartsTable ? (
                <PriceRequestComponent
                  selectedNonePriceItem={selectedTableRows?.selectedRows}
                  onSuccessCallback={() =>
                    setSelectedTableRows({ isSelectedAll: false, selectedRows: [] })
                  }
                />
              ) : (
                <></>
              )}
              {renderRibbon && renderRibbon()}
            </>
          }
        />
      </TableContainer>
      {partDetail.partFlyout && (
        <PartViewFlyout
          open={partDetail.partFlyout}
          handleFlyoutClose={() => setPartDetail({})}
          machineId={partDetail.machineId}
          key={partDetail.productId}
          priceUnit={partDetail.priceUnit}
          productId={partDetail.productId}
          other={partDetail}
        />
      )}
    </Root>
  );
};

export default MachinePartsTable;
